import React, { Component } from "react";
import {
  Container,
  Row,
  Col,
  Input,
  Button,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Progress,
  Spinner
} from "reactstrap";
import {
  errorNotification,
  infoNotification,
  successNotification
} from "../../../utils/notifications";

interface Props {
  validarProductosCargaMasiva: (formData: FormData) => Promise<any>;
  procesarProductosCargaMasiva: (formData: FormData) => Promise<any>;
  searching: boolean;
  saving: boolean;
  validationResult: any;
  processingResult: any;
}

interface State {
  selectedFile: File | null;
  fileName: string;
  isValid: boolean;
  validando: boolean;
  procesando: boolean;
  resolvedErrors: number[];
  removedErrors: number[];
  showUndo: boolean;
  fileInputKey: string;
  showInstructionModal: boolean;
  allErrorsResolved: boolean;
  needsNewFile: boolean;
  originalErrorCount: number;
  resetValidationResult: boolean;
  showProcessingModal: boolean;
  processingMessage: string;
  processingPercent: number;
  processingTimeElapsed: number;
  processingTimer: any;
}

class ProductosCargaMasivaComponent extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      selectedFile: null,
      fileName: "",
      isValid: false,
      validando: false,
      procesando: false,
      resolvedErrors: [],
      removedErrors: [],
      showUndo: false,
      fileInputKey: Date.now().toString(),
      showInstructionModal: false,
      allErrorsResolved: false,
      needsNewFile: false,
      originalErrorCount: 0,
      resetValidationResult: false,
      showProcessingModal: false,
      processingMessage: "",
      processingPercent: 0,
      processingTimeElapsed: 0,
      processingTimer: null
    };
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (this.props.validationResult !== prevProps.validationResult) {
      const result = this.props.validationResult;

      if (this.state.resetValidationResult) {
        return;
      }

      if (result) {
        const isValid = result.valid === true || result.isValid === true;

        let originalErrorCount = this.state.originalErrorCount;
        if (
          !prevProps.validationResult &&
          result.errors &&
          result.errors.length > 0
        ) {
          originalErrorCount = result.errors.length;
        }

        this.setState({
          isValid: isValid && !this.state.needsNewFile,
          originalErrorCount
        });

        if (this.state.showProcessingModal && this.state.validando) {
          this.closeProcessingModal(
            isValid
              ? "¡Validación completada correctamente!"
              : `Validación completada. Se encontraron ${
                  result.errors ? result.errors.length : 0
                } errores.`
          );
        }
      }
    }

    if (
      prevProps.searching &&
      !this.props.searching &&
      this.state.showProcessingModal &&
      this.state.validando
    ) {
      this.closeProcessingModal();
    }

    if (
      prevProps.saving &&
      !this.props.saving &&
      this.state.showProcessingModal &&
      this.state.procesando
    ) {
      this.closeProcessingModal();
    }

    const { validationResult } = this.props;
    const {
      resolvedErrors,
      allErrorsResolved,
      originalErrorCount,
      resetValidationResult
    } = this.state;

    if (
      !resetValidationResult &&
      validationResult &&
      validationResult.errors &&
      validationResult.errors.length > 0 &&
      resolvedErrors.length === validationResult.errors.length &&
      !allErrorsResolved &&
      originalErrorCount > 0
    ) {
      this.setState({
        allErrorsResolved: true,
        showInstructionModal: true,
        needsNewFile: true,
        isValid: false
      });
    }
  }

  componentWillUnmount() {
    if (this.state.processingTimer) {
      clearInterval(this.state.processingTimer);
    }
  }

  handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      this.setState({
        selectedFile: file,
        fileName: file.name,
        isValid: false,
        resolvedErrors: [],
        removedErrors: [],
        showUndo: false,
        allErrorsResolved: false,
        needsNewFile: false,
        originalErrorCount: 0,
        resetValidationResult: false
      });
    } else {
      this.setState({
        selectedFile: null,
        fileName: "",
        isValid: false,
        resolvedErrors: [],
        removedErrors: [],
        showUndo: false,
        allErrorsResolved: false,
        needsNewFile: false,
        originalErrorCount: 0,
        resetValidationResult: false
      });
    }
  };

  resetFileInput = () => {
    this.setState(
      {
        resetValidationResult: true
      },
      () => {
        this.setState({
          selectedFile: null,
          fileName: "",
          isValid: false,
          resolvedErrors: [],
          removedErrors: [],
          showUndo: false,
          fileInputKey: Date.now().toString(),
          allErrorsResolved: false,
          needsNewFile: false,
          originalErrorCount: 0,
          resetValidationResult: false
        });
      }
    );
  };

  showProcessingModal = (message: string, isValidating: boolean = true) => {
    if (this.state.processingTimer) {
      clearInterval(this.state.processingTimer);
    }

    const timer = setInterval(() => {
      this.setState(prevState => ({
        processingTimeElapsed: prevState.processingTimeElapsed + 1,

        processingPercent: Math.min(
          95,
          prevState.processingPercent + (100 - prevState.processingPercent) / 20
        )
      }));
    }, 1000);

    this.setState({
      showProcessingModal: true,
      processingMessage: message,
      processingPercent: 0,
      processingTimeElapsed: 0,
      processingTimer: timer
    });
  };

  closeProcessingModal = (finalMessage?: string) => {
    if (this.state.processingTimer) {
      clearInterval(this.state.processingTimer);
    }

    if (finalMessage) {
      this.setState({
        processingMessage: finalMessage,
        processingPercent: 100
      });

      setTimeout(() => {
        this.setState({
          showProcessingModal: false,
          processingTimer: null
        });
      }, 1500);
    } else {
      this.setState({
        showProcessingModal: false,
        processingTimer: null
      });
    }
  };

  formatElapsedTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  handleValidate = async (e: React.FormEvent) => {
    e.preventDefault();

    const { selectedFile } = this.state;
    if (!selectedFile) {
      errorNotification("Por favor, seleccione un archivo para validar");
      return;
    }

    this.setState({ validando: true, allErrorsResolved: false });
    infoNotification("Validando archivo");

    let fileSize = "";
    if (selectedFile.size < 1024 * 1024) {
      fileSize = `${Math.round(selectedFile.size / 1024)} KB`;
    } else {
      fileSize = `${Math.round((selectedFile.size / (1024 * 1024)) * 10) /
        10} MB`;
    }

    this
      .showProcessingModal(`Validando archivo "${selectedFile.name}" (${fileSize})...
    
Este proceso puede tardar varios minutos para archivos grandes.
Por favor, no cierre esta ventana ni recargue la página.`);

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      await this.props.validarProductosCargaMasiva(formData);

      const result = this.props.validationResult;
      if (result) {
        const isValid = result.valid === true || result.isValid === true;

        if (isValid) {
          successNotification("El archivo ha sido validado correctamente");
        } else if (result.errors && result.errors.length > 0) {
          errorNotification(
            `El archivo contiene ${result.errors.length} errores que deben corregirse`
          );
        } else {
          errorNotification(
            "El archivo contiene errores, pero no se pudieron procesar los detalles"
          );
        }
      }
    } catch (error) {
      console.error("Error al validar el archivo:", error);
      errorNotification("Ocurrió un error al validar el archivo");
      this.closeProcessingModal("Se produjo un error durante la validación");
    } finally {
      this.setState({ validando: false });
    }
  };

  handleProcess = async (e: React.FormEvent) => {
    e.preventDefault();

    const { isValid, needsNewFile } = this.state;
    const filteredErrors = this.getFilteredErrors();

    if (needsNewFile) {
      errorNotification("Debe cargar el archivo corregido antes de procesar");
      this.setState({ showInstructionModal: true });
      return;
    }

    if (!isValid && filteredErrors.length > 0) {
      errorNotification("El archivo aún tiene errores por resolver");
      return;
    }

    this.setState({ procesando: true });
    infoNotification("Procesando archivo");

    this.showProcessingModal(
      `Procesando archivo "${this.state.fileName}"...
Este proceso puede tardar varios minutos. 
El archivo se está cargando en la base de datos.
Por favor, no cierre esta ventana ni recargue la página.`,
      false
    );

    const formData = new FormData();
    formData.append("file", this.state.selectedFile);

    try {
      await this.props.procesarProductosCargaMasiva(formData);

      successNotification("Archivo procesado correctamente");
      this.closeProcessingModal("¡Archivo procesado correctamente!");

      this.resetFileInput();
    } catch (error) {
      console.error("Error al procesar el archivo:", error);
      errorNotification("Ocurrió un error al procesar el archivo");
      this.closeProcessingModal("Se produjo un error durante el procesamiento");
    } finally {
      this.setState({ procesando: false });
    }
  };

  handleResolvedCheck = (index: number) => {
    const { resolvedErrors, removedErrors } = this.state;

    if (resolvedErrors.includes(index)) {
      this.setState({
        resolvedErrors: resolvedErrors.filter(i => i !== index)
      });
    } else {
      const newResolvedErrors = [...resolvedErrors, index];
      const newRemovedErrors = [...removedErrors, index];

      this.setState({
        resolvedErrors: newResolvedErrors,
        removedErrors: newRemovedErrors,
        showUndo: true
      });
    }
  };

  handleUndo = () => {
    const { removedErrors, resolvedErrors } = this.state;
    if (removedErrors.length === 0) return;

    const lastRemovedError = removedErrors[removedErrors.length - 1];

    this.setState({
      resolvedErrors: resolvedErrors.filter(i => i !== lastRemovedError),
      removedErrors: removedErrors.filter(i => i !== lastRemovedError),
      showUndo: removedErrors.length > 1,
      allErrorsResolved: false,
      needsNewFile: false
    });
  };

  getFilteredErrors = () => {
    if (this.state.resetValidationResult) {
      return [];
    }

    const { validationResult } = this.props;
    const { resolvedErrors } = this.state;

    if (!validationResult || !validationResult.errors) return [];

    return validationResult.errors.filter(
      (_, index) => !resolvedErrors.includes(index)
    );
  };

  isResultValid = () => {
    const { validationResult } = this.props;
    const { needsNewFile, resetValidationResult } = this.state;

    if (resetValidationResult) return false;
    if (!validationResult) return false;
    if (needsNewFile) return false;

    return validationResult.valid === true || validationResult.isValid === true;
  };

  toggleInstructionModal = () => {
    if (this.state.showInstructionModal && this.state.needsNewFile) {
      errorNotification("Debe cargar el archivo corregido para continuar");
      return;
    }

    this.setState(prevState => ({
      showInstructionModal: !prevState.showInstructionModal
    }));
  };

  handleClearResolvedErrors = () => {
    this.setState(
      {
        showInstructionModal: false
      },
      () => {
        this.resetFileInput();

        infoNotification("Seleccione el archivo Excel corregido");
      }
    );
  };

  render() {
    const {
      selectedFile,
      fileName,
      validando,
      procesando,
      resolvedErrors,
      showUndo,
      fileInputKey,
      showInstructionModal,
      allErrorsResolved,
      needsNewFile,
      resetValidationResult,
      showProcessingModal,
      processingMessage,
      processingPercent,
      processingTimeElapsed
    } = this.state;

    const { validationResult } = this.props;
    const filteredErrors = this.getFilteredErrors();

    const showValidationResults = !resetValidationResult && validationResult;

    return (
      <Container fluid className="p-4">
        <Card className="shadow-sm mb-4">
          <CardHeader className="bg-white">
            <h2 className="mb-0">Importar Productos</h2>
          </CardHeader>
          <CardBody>
            <Row className="mb-4">
              <Col md={8}>
                <FormGroup>
                  <Label for="fileInput" className="d-block mb-2 fw-bold">
                    Seleccionar archivo Excel (.xlsx)
                  </Label>
                  <div className="custom-file-container">
                    <div className="input-group">
                      <div className="custom-file flex-grow-1">
                        <Input
                          type="file"
                          key={fileInputKey}
                          id="fileInput"
                          accept=".xlsx"
                          onChange={this.handleFileChange}
                          className="form-control"
                          style={{ cursor: "pointer" }}
                        />
                      </div>
                      {selectedFile && (
                        <Button
                          color="outline-secondary"
                          onClick={this.resetFileInput}
                          title="Eliminar archivo seleccionado"
                        >
                          ×
                        </Button>
                      )}
                    </div>
                    {fileName && (
                      <small className="form-text text-muted mt-1">
                        Archivo seleccionado: <strong>{fileName}</strong>
                      </small>
                    )}
                  </div>
                </FormGroup>
              </Col>

              <Col md={4} className="d-flex align-items-end">
                <div className="btn-group w-100">
                  <Button
                    color="primary"
                    className="px-4"
                    onClick={this.handleValidate}
                    disabled={validando || !selectedFile || procesando}
                  >
                    {validando ? "Validando..." : "Validar"}
                  </Button>

                  <Button
                    color="success"
                    className="px-4"
                    onClick={this.handleProcess}
                    disabled={
                      !this.isResultValid() ||
                      validando ||
                      procesando ||
                      needsNewFile
                    }
                  >
                    {procesando ? "Procesando..." : "Procesar"}
                  </Button>
                </div>
              </Col>
            </Row>

            {showValidationResults && !this.isResultValid() && !needsNewFile && (
              <div>
                {showUndo && (
                  <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded mb-3 shadow-sm">
                    <p className="m-0">
                      <span className="badge bg-secondary me-2">
                        {this.state.removedErrors.length}
                      </span>
                      {this.state.removedErrors.length === 1
                        ? "error marcado"
                        : "errores marcados"}{" "}
                      como resuelto
                    </p>
                    <Button
                      color="outline-secondary"
                      size="sm"
                      onClick={this.handleUndo}
                    >
                      Deshacer
                    </Button>
                  </div>
                )}

                <div className="alert alert-danger p-3 mb-4 shadow-sm">
                  <h4 className="text-danger mb-3 d-flex align-items-center">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    El archivo "{validationResult.fileName}" contiene{" "}
                    {filteredErrors.length} errores:
                  </h4>

                  {filteredErrors.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-striped table-bordered">
                        <thead className="table-light">
                          <tr>
                            <th width="20%">Hoja</th>
                            <th width="10%">Celda</th>
                            <th width="15%">Tipo de Error</th>
                            <th>Descripción</th>
                            <th width="10%" className="text-center">
                              Resuelto
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredErrors.map((error, index) => {
                            const originalIndex = validationResult.errors.indexOf(
                              error
                            );
                            return (
                              <tr
                                key={
                                  error.sheetName +
                                  "-" +
                                  error.cellReference +
                                  "-" +
                                  originalIndex
                                }
                              >
                                <td>{error.sheetName}</td>
                                <td>{error.cellReference}</td>
                                <td>
                                  <code>{error.errorType}</code>
                                </td>
                                <td>{error.errorDescription}</td>
                                <td className="text-center">
                                  <div className="form-check d-flex justify-content-center">
                                    <Input
                                      type="checkbox"
                                      checked={resolvedErrors.includes(
                                        originalIndex
                                      )}
                                      onChange={() =>
                                        this.handleResolvedCheck(originalIndex)
                                      }
                                      className="form-check-input"
                                    />
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="alert alert-success">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-check-circle-fill me-2 fs-4"></i>
                        <div>
                          <h5 className="mb-1">
                            ¡Todos los errores han sido marcados como resueltos!
                          </h5>
                          <p className="mb-0">
                            Ahora necesitas corregir el archivo Excel y volver a
                            cargarlo para verificar que los errores se han
                            solucionado realmente.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {showValidationResults && this.isResultValid() && (
              <div className="alert alert-success shadow-sm">
                <div className="d-flex align-items-center">
                  <i className="bi bi-check-circle-fill me-2 fs-4"></i>
                  <div>
                    <h5 className="mb-1">Validación exitosa</h5>
                    <p className="mb-0">
                      El archivo "{validationResult.fileName}" ha sido validado
                      correctamente y está listo para ser procesado.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardBody>
        </Card>

        {/* Modal de instrucciones - No se puede cerrar si se necesita un nuevo archivo */}
        <Modal
          isOpen={showInstructionModal}
          toggle={this.toggleInstructionModal}
          backdrop={needsNewFile ? "static" : true}
          keyboard={!needsNewFile}
        >
          <ModalHeader
            toggle={needsNewFile ? undefined : this.toggleInstructionModal}
          >
            <i className="bi bi-info-circle-fill me-2"></i>
            Instrucciones para corregir errores
          </ModalHeader>
          <ModalBody>
            <div className="text-center mb-4">
              <i
                className="bi bi-file-earmark-excel text-success"
                style={{ fontSize: "3rem" }}
              ></i>
            </div>
            <h5 className="text-center mb-3">
              ¡Has marcado todos los errores como resueltos!
            </h5>
            <p>
              Para continuar con el proceso, <strong>debes</strong> seguir estos
              pasos:
            </p>
            <ol className="mb-4">
              <li className="mb-2">
                Abre el archivo Excel original en tu computadora.
              </li>
              <li className="mb-2">
                Corrige todos los errores que has identificado en la tabla.
              </li>
              <li className="mb-2">Guarda los cambios en el archivo Excel.</li>
              <li className="mb-2">
                Haz clic en "Cargar archivo corregido" a continuación.
              </li>
              <li className="mb-2">Selecciona el archivo Excel corregido.</li>
              <li className="mb-2">
                Haz clic en "Validar" para verificar que todos los errores han
                sido resueltos.
              </li>
              <li>
                Si no hay más errores, podrás hacer clic en "Procesar" para
                finalizar.
              </li>
            </ol>
            <div className="alert alert-warning">
              <div className="d-flex">
                <i className="bi bi-exclamation-triangle-fill me-2 mt-1"></i>
                <div>
                  <strong>¡Importante!</strong> Marcar errores como resueltos no
                  corrige el archivo automáticamente. Es necesario que realices
                  las correcciones manualmente en el archivo Excel y lo cargues
                  nuevamente.
                </div>
              </div>
            </div>

            {needsNewFile && (
              <div className="alert alert-danger mt-3">
                <div className="d-flex">
                  <i className="bi bi-exclamation-triangle-fill me-2 mt-1"></i>
                  <div>
                    <strong>Atención:</strong> Debes cargar el archivo corregido
                    para continuar. No podrás procesar el archivo actual,
                    incluso si todos los errores están marcados como resueltos.
                  </div>
                </div>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            {!needsNewFile && (
              <Button color="secondary" onClick={this.toggleInstructionModal}>
                Entendido, revisaré la tabla
              </Button>
            )}
            <Button
              color={needsNewFile ? "danger" : "primary"}
              onClick={this.handleClearResolvedErrors}
            >
              Cargar archivo corregido
            </Button>
          </ModalFooter>
        </Modal>

        <Modal
          isOpen={showProcessingModal}
          backdrop="static"
          keyboard={false}
          centered
          size="lg"
        >
          <ModalBody className="p-4">
            <div className="text-center mb-4">
              {/* <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} /> */}
            </div>
            <h4 className="text-center mb-4">{processingMessage}</h4>

            <div className="mb-3">
              <Progress
                animated
                color="info"
                value={processingPercent}
                className="mb-2"
                style={{ height: "10px" }}
              />
              <div className="d-flex justify-content-between">
                <small className="text-muted">Procesando archivo...</small>
                <small className="text-muted">
                  Tiempo transcurrido:{" "}
                  {this.formatElapsedTime(processingTimeElapsed)}
                </small>
              </div>
            </div>

            <div className="alert alert-warning">
              <div className="d-flex align-items-center">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                <div>
                  <strong>¡No cierre esta ventana!</strong> Esta operación puede
                  tardar varios minutos para archivos grandes. Espere a que el
                  sistema termine de procesar su archivo.
                </div>
              </div>
            </div>

            {processingTimeElapsed > 30 && (
              <div className="alert alert-info mt-3">
                <div className="d-flex align-items-center">
                  <i className="bi bi-info-circle-fill me-2"></i>
                  <div>
                    <strong>El proceso continúa en marcha.</strong> Los archivos
                    grandes pueden tomar varios minutos. Por favor, sea paciente
                    mientras se completa la operación.
                  </div>
                </div>
              </div>
            )}
          </ModalBody>
        </Modal>
      </Container>
    );
  }
}

export default ProductosCargaMasivaComponent;
